import { useCallback, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type { WooProduct, WooStoreConfig, WooStoreCurrency } from "../types";

interface WooProductsCatalog {
  products: WooProduct[];
  currency: WooStoreCurrency | null;
}

const EMPTY_PRODUCTS_CATALOG: WooProductsCatalog = {
  products: [],
  currency: null,
};

function mapProductsError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível carregar os produtos.";
}

/**
 * Produtos de uma loja WooCommerce + busca local (e via API quando recarrega).
 * Cada carga pede a moeda atual e só então a listagem de produtos.
 */
export function useWooCommerceProducts(store: WooStoreConfig | null) {
  const [searchTerm, setSearchTerm] = useState("");

  const loadTask = useCallback(
    async (search?: string): Promise<WooProductsCatalog> => {
      if (!store) return EMPTY_PRODUCTS_CATALOG;

      const currency = await woocommerceStoreService.getCurrentCurrency(store);
      const products = await woocommerceStoreService.listProducts(store, search);
      return { products, currency };
    },
    [store],
  );

  const {
    run: loadProducts,
    loading,
    error,
    data: catalog,
    reset,
  } = useAsyncTask({
    task: loadTask,
    initialData: EMPTY_PRODUCTS_CATALOG,
    mapError: mapProductsError,
  });

  const products = catalog?.products ?? [];
  const currency = catalog?.currency ?? null;

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    return woocommerceStoreService.filterProductsLocally(products, searchTerm);
  }, [products, searchTerm]);

  const reload = useCallback(
    (apiSearch?: string) => {
      if (!store) return Promise.resolve(EMPTY_PRODUCTS_CATALOG);
      return loadProducts(apiSearch);
    },
    [store, loadProducts],
  );

  return {
    products: filteredProducts,
    allProducts: products,
    currency,
    searchTerm,
    setSearchTerm,
    loadProducts: reload,
    loading,
    error: error ?? null,
    reset,
  };
}
